import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getToken } from "../api/utils";
import LoginModal from "../components/LoginModal";
import RSVPModal from "../components/RSVPModal";
import ExperienceMediaSlider from "../components/ExperienceMediaSlider";
import InviteCommentSection from "../components/InviteCommentSection";
import { getInviteRevealById } from "../data/inviteThemes";
import "./InviteExperience.css";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

const inferMediaType = (raw = {}, url = "") => {
  const explicitType = `${raw.type || raw.media_type || ""}`.toLowerCase();
  if (explicitType.includes("image")) return "image";
  if (explicitType.includes("video")) return "video";
  if (raw.is_video === true) return "video";

  const cleanUrl = `${url}`.split("?")[0].toLowerCase();
  if (/\.(png|jpe?g|webp|gif|svg|avif)$/i.test(cleanUrl)) return "image";
  if (/\.(mp4|webm|mov|m4v|ogv)$/i.test(cleanUrl)) return "video";
  return "image";
};

const normalizeMediaItem = (raw = {}) => {
  if (typeof raw === "string") {
    raw = { url: raw };
  }
  const url = raw.url || raw.file_path || raw.filePath || raw.video_url;
  if (!url) return null;

  return {
    id: raw.id || raw.media_id || url,
    url,
    title: raw.title || raw.caption || "",
    type: inferMediaType(raw, url),
    visibility: raw.visibility || raw.access || "public",
    poster: raw.poster,
  };
};

const normalizeComment = (raw = {}) => ({
    id: raw.id || raw.comment_id || `${raw.author?.user_id || raw.author?.id || raw.user_id || "anon"}-${raw.timestamp || raw.created_at || Date.now()}`,
    text: raw.text || raw.body || raw.comment || "",
    visibility: raw.visibility || raw.access || "public",
    createdAt: raw.created_at || raw.timestamp || new Date().toISOString(),
    likes:
      raw.likes ??
      raw.likes_count ??
      raw.like_count ??
      raw.likes_total ??
      0,
    likedByCurrentUser:
      raw.liked_by_current_user ??
      raw.liked_by_me ??
      raw.liked ??
      false,
  author: {
    id: raw.author?.user_id || raw.author?.id || raw.user_id || raw.created_by || "guest",
    name: raw.author?.name || raw.user_name || raw.display_name || "Guest",
  },
  replies: Array.isArray(raw.replies)
    ? raw.replies.map((reply) => normalizeComment(reply))
    : [],
});

const addReplyToTree = (list, parentId, reply) =>
  list.map((comment) => {
    if (comment.id === parentId) {
      return { ...comment, replies: [reply, ...(comment.replies || [])] };
    }
    if (comment.replies) {
      const updatedReplies = addReplyToTree(comment.replies, parentId, reply);
      if (updatedReplies !== comment.replies) {
        return { ...comment, replies: updatedReplies };
      }
    }
    return comment;
  });

const replaceCommentById = (list, targetId, replacement) =>
  list.map((comment) => {
    if (comment.id === targetId) {
      return { ...replacement, replies: comment.replies || [] };
    }
    if (comment.replies) {
      return { ...comment, replies: replaceCommentById(comment.replies, targetId, replacement) };
    }
    return comment;
  });

const removeCommentById = (list, targetId) =>
  list
    .map((comment) => {
      if (comment.id === targetId) return null;
      if (comment.replies) {
        return { ...comment, replies: removeCommentById(comment.replies, targetId) };
      }
      return comment;
    })
    .filter(Boolean);

const toggleLikeInTree = (list, targetId) =>
  list.map((comment) => {
    if (comment.id === targetId) {
      const nextLiked = !comment.likedByCurrentUser;
      const likes = nextLiked ? (comment.likes || 0) + 1 : Math.max(0, (comment.likes || 0) - 1);
      return { ...comment, likedByCurrentUser: nextLiked, likes };
    }
    if (comment.replies) {
      return { ...comment, replies: toggleLikeInTree(comment.replies, targetId) };
    }
    return comment;
  });

const hexToRgba = (hex, alpha) => {
  const normalized = String(hex || "").replace("#", "");
  if (normalized.length !== 6) return `rgba(255, 247, 234, ${alpha})`;
  const red = parseInt(normalized.slice(0, 2), 16);
  const green = parseInt(normalized.slice(2, 4), 16);
  const blue = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const getRoyalThemeVars = (theme) => {
  const tokens = theme?.tokens || {};
  return {
    "--royal-bg": tokens.background || "#2b0d17",
    "--royal-bg-soft": tokens.backgroundAlt || "#4b1830",
    "--royal-surface": hexToRgba(tokens.surface || "#fff7ea", 0.88),
    "--royal-surface-strong": hexToRgba(tokens.surface || "#fffbf5", 0.96),
    "--royal-gold": tokens.accent || "#d9b36a",
    "--royal-gold-deep": tokens.accentAlt || "#a87a2d",
    "--royal-ink": tokens.text || "#2f1d18",
    "--royal-muted": tokens.muted || "#7f665f",
    "--royal-border": tokens.border || "rgba(169, 122, 45, 0.28)",
    "--royal-heading-font": tokens.headingFont || "Georgia, Times New Roman, serif",
  };
};

export default function InviteExperience({ theme, revealId }) {
  const { token, eventId: routeEventId } = useParams();
  const { user, isAuthenticated } = useAuth();
  const isLoggedIn = isAuthenticated;

  const [mainMedia, setMainMedia] = useState(null);
  const [mediaList, setMediaList] = useState([]);
  const [eventMeta, setEventMeta] = useState({
    title: "Your Video Invitation",
    description: "Share unforgettable moments with your guests.",
    date: "",
  });
  const [guestData, setGuestData] = useState(null);
  const [loadingInvite, setLoadingInvite] = useState(false);
  const [inviteError, setInviteError] = useState("");

  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentError, setCommentError] = useState("");
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  const [isInvitationOpened, setIsInvitationOpened] = useState(false);
  const [isCurtainOpening, setIsCurtainOpening] = useState(false);
  const isHostPreview = !!guestData?.viewer?.is_host;
  const currentUser = useMemo(
    () => ({
      id: user?.id || user?.token || "guest",
      name: user?.name || "You",
      isHost: user?.isHost || isHostPreview || false,
    }),
    [user, isHostPreview]
  );

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRSVPModal, setShowRSVPModal] = useState(false);
  const sliderClientConfig = useMemo(() => {
    if (typeof window === "undefined") return {};
    return (
      window.__CLIENT_CONFIG__?.inviteSlider ||
      window.clientConfig?.inviteSlider ||
      window.__CLIENT_CONFIG__?.slider ||
      {}
    );
  }, []);

  const eventId = guestData?.event?.event_id || guestData?.event?.id || routeEventId;
  const canLoadComments = Boolean(eventId && !inviteError);
  const activeReveal = getInviteRevealById(revealId || guestData?.event?.reveal_id || "curtain-open");
  const royalThemeVars = useMemo(() => getRoyalThemeVars(theme), [theme]);

  const fetchInvite = useCallback(async () => {
    if (!token && !routeEventId) {
      setInviteError("No invitation link or event preview provided.");
      setGuestData(null);
      setMediaList([]);
      setMainMedia(null);
      return;
    }

    setLoadingInvite(true);
    setInviteError("");
    setCommentError("");
    try {
      const endpoint = token
        ? `${API_BASE}/events/invite/${token}`
        : `${API_BASE}/events/${routeEventId}/experience`;
      const response = await fetch(endpoint, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${getToken() || ""}`,
        },
      });

      if (!response.ok) {
        let msg = `Invite fetch failed (${response.status})`;
        try {
          const errJson = await response.json();
          msg = errJson.detail || errJson.message || msg;
        } catch (_e) {
          // ignore parsing errors
        }
        throw new Error(msg);
      }

      const payload = await response.json();
      setGuestData(payload);
    } catch (error) {
      setInviteError(error.message || "Could not load invitation.");
      setGuestData(null);
      setMediaList([]);
      setMainMedia(null);
    } finally {
      setLoadingInvite(false);
    }
  }, [token, routeEventId]);

  const fetchComments = useCallback(async () => {
    if (!eventId) {
      setComments([]);
      setCommentError("");
      setLoadingComments(false);
      return;
    }

    setLoadingComments(true);
    setCommentError("");
    try {
      const headers = { Accept: "application/json" };
      const bearer = getToken();
      if (bearer) headers.Authorization = `Bearer ${bearer}`;
      const response = await fetch(`${API_BASE}/events/${eventId}/comments`, {
        headers,
      });
      if (!response.ok) {
        throw new Error("Unable to load comments");
      }
      const payload = await response.json();
      const rawComments = payload || payload.comments || payload.data || [];
      setComments(rawComments.map((raw) => normalizeComment(raw)));
      setPendingDeleteIds([]);
    } catch (error) {
      setCommentError(error.message || "Unable to load comments.");
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (!guestData) {
      setMainMedia(null);
      setMediaList([]);
      return;
    }

    const event = guestData.event || {};
    const heroUrl = event.cover_media || event.image_url || event.preview;
    const hero = heroUrl
      ? {
          id: event.id || heroUrl,
          url: heroUrl,
          type: inferMediaType(event, heroUrl),
          visibility: "public",
        }
      : null;

    const rawMedia = Array.isArray(guestData.media) ? guestData.media : [];
    const normalized = rawMedia
      .map((item) => normalizeMediaItem(item))
      .filter((item) => item?.url);
    const filteredAdditional = normalized.filter(
      (item) => !hero || (item.id !== hero.id && item.url !== hero.url)
    );

    setMainMedia(hero);
    setMediaList(filteredAdditional);
    setEventMeta({
      title: event.event_title || event.title || "Your Video Invitation",
      description:
        event.description ||
        guestData.description ||
        "A cinematic invite delivered straight to your guests.",
      date: event.event_date || event.date || "",
      hostName: event.host_name || "",
      guestId: guestData?.guest?.event_guest_id || guestData?.guest?.id,
    });
  }, [guestData]);

  useEffect(() => {
    fetchInvite();
  }, [fetchInvite, isLoggedIn]);

  useEffect(() => {
    if (!canLoadComments) {
      setComments([]);
      setCommentError("");
      setLoadingComments(false);
      return;
    }

    fetchComments();
  }, [canLoadComments, fetchComments]);

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    fetchInvite();
  };

  const handleRSVPClick = () => {
    if (isHostPreview) {
      return;
    }
    if (!isLoggedIn) {
      setShowLoginModal(true);
    } else {
      setShowRSVPModal(true);
    }
  };

  const hasRSVPed =
    !!guestData?.guest?.rsvp_status && guestData.guest.rsvp_status !== "pending";
  const rsvpButtonText = hasRSVPed ? "Change RSVP" : "RSVP now";
  const rsvpButtonClass = `primary-btn ${hasRSVPed ? "rsvp-changed" : ""}`;

  const handleRSVPSuccess = useCallback(() => {
    fetchInvite();
  }, [fetchInvite]);

  const handleCommentSubmit = async ({ text, visibility, parentId }) => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }

    setCommentError("");
    const tempId = `temp-${Date.now()}`;
    const optimisticComment = {
      id: tempId,
      text,
      visibility,
      createdAt: new Date().toISOString(),
      likes: 0,
      likedByCurrentUser: false,
      author: {
        id: currentUser.id,
        name: currentUser.name,
      },
      replies: [],
    };

    setComments((prev) =>
      parentId ? addReplyToTree(prev, parentId, optimisticComment) : [optimisticComment, ...prev]
    );

    try {
      const headers = { "Content-Type": "application/json" };
      const bearer = getToken();
      if (bearer) headers.Authorization = `Bearer ${bearer}`;
      const endpoint = parentId
        ? `${API_BASE}/comments/${parentId}/replies`
        : `${API_BASE}/events/${eventId}/comments`;
      const response = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify({
          body: text,
          visibility,
        }),
      });
      if (!response.ok) {
        throw new Error("Unable to post comment");
      }
      const saved = await response.json();
      const normalized = normalizeComment(saved);
      setComments((prev) => replaceCommentById(prev, tempId, normalized));
    } catch (error) {
      setCommentError(error.message || "Unable to post comment, please retry.");
      setComments((prev) => removeCommentById(prev, tempId));
    }
  };

  const handleLikeToggle = async (commentId) => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }

    setComments((prev) => toggleLikeInTree(prev, commentId));
    try {
      const headers = { "Content-Type": "application/json" };
      const bearer = getToken();
      if (bearer) headers.Authorization = `Bearer ${bearer}`;
      await fetch(`${API_BASE}/comments/${commentId}/like`, {
        method: "POST",
        headers,
        body: JSON.stringify({ event_id: eventId, event_token: token }),
      });
    } catch (error) {
      setComments((prev) => toggleLikeInTree(prev, commentId));
    }
  };

  const handleDeleteComment = useCallback(
    async (commentId) => {
      if (!isLoggedIn) {
        setShowLoginModal(true);
        return;
      }

      if (!eventId) {
        setCommentError("Missing event id");
        return;
      }

      if (pendingDeleteIds.includes(commentId)) {
        return;
      }

      if (!window.confirm("Delete this comment?")) {
        return;
      }

      setCommentError("");
      setPendingDeleteIds((prev) => [...prev, commentId]);

      try {
        const headers = { "Content-Type": "application/json" };
        const bearer = getToken();
        if (bearer) headers.Authorization = `Bearer ${bearer}`;
        const response = await fetch(`${API_BASE}/comments/${commentId}`, {
          method: "DELETE",
          headers,
          body: JSON.stringify({ event_id: eventId, event_token: token }),
        });
        if (!response.ok) {
          throw new Error("Unable to delete comment");
        }
        setComments((prev) => removeCommentById(prev, commentId));
        setPendingDeleteIds((prev) => prev.filter((id) => id !== commentId));
      } catch (error) {
        setCommentError(error.message || "Unable to delete comment.");
        setPendingDeleteIds((prev) => prev.filter((id) => id !== commentId));
        fetchComments();
      }
    },
    [eventId, token, fetchComments, isLoggedIn, pendingDeleteIds]
  );

  const guestForRSVP = useMemo(() => {
    const guest = guestData?.guest || {};
    return {
      event_guest_id: guest.event_guest_id || eventMeta.guestId || "0",
      rsvp_status: guest.rsvp_status || "pending",
      rsvp_adult_count: guest.rsvp_adult_count ?? "",
      rsvp_kids_count: guest.rsvp_kids_count ?? "",
      rsvp_allergies: guest.rsvp_allergies || "",
      rsvp_message: guest.rsvp_message || "",
    };
  }, [eventMeta.guestId, guestData]);

  const guestName = isHostPreview
    ? eventMeta.hostName || "Host Preview"
    : guestData?.guest?.guest_name || guestData?.guest?.name || "Honoured Guest";

  useEffect(() => {
    if (isInvitationOpened || isCurtainOpening) {
      return;
    }

    const startTimer = window.setTimeout(() => {
      setIsCurtainOpening(true);
    }, 700);

    const revealDuration = activeReveal.id === "petal-blessing" ? 5600 : 3600;

    const finishTimer = window.setTimeout(() => {
      setIsInvitationOpened(true);
      setIsCurtainOpening(false);
    }, revealDuration);

    return () => {
      window.clearTimeout(startTimer);
      window.clearTimeout(finishTimer);
    };
  }, [activeReveal.id, isCurtainOpening, isInvitationOpened]);

  return (
    <div
      className={`invite-experience-page ${isInvitationOpened ? "is-open" : "is-sealed"} ${
        isCurtainOpening ? "is-opening" : ""
      }`}
      style={royalThemeVars}
    >
      {!isInvitationOpened && (
        <section
          className={`invitation-seal-screen reveal-${activeReveal.id}`}
          aria-label="Open invitation screen"
        >
          {activeReveal.id === "curtain-open" ? (
            <>
              <div className="curtain-panel curtain-left"></div>
              <div className="curtain-panel curtain-right"></div>
            </>
          ) : null}
          <div className="curtain-center-glow"></div>
          {activeReveal.id === "confetti-pop" ? (
            <div className="royal-reveal-confetti" aria-hidden="true">
              {[...Array(20)].map((_, index) => (
                <span
                  key={index}
                  style={{
                    "--i": index,
                    left: `${8 + ((index * 11) % 84)}%`,
                    top: `${12 + ((index * 17) % 72)}%`,
                  }}
                />
              ))}
            </div>
          ) : null}
          {activeReveal.id === "spotlight-rise" ? <div className="royal-reveal-spotlight" /> : null}
          {activeReveal.id === "floral-bloom" ? (
            <div className="royal-reveal-florals">
              <span />
              <span />
              <span />
            </div>
          ) : null}
          {activeReveal.id === "slide-up-card" ? <div className="royal-reveal-card" /> : null}
          {activeReveal.id === "sparkle-frame" ? (
            <div className="royal-reveal-frame">
              <span />
              <span />
              <span />
            </div>
          ) : null}
          {activeReveal.id === "balloon-float" ? (
            <div className="royal-reveal-balloons">
              {[...Array(11)].map((_, index) => (
                <span key={index} style={{ "--i": index }} />
              ))}
            </div>
          ) : null}
          {activeReveal.id === "petal-blessing" ? (
            <div className="royal-reveal-petals" aria-hidden="true">
              {[...Array(84)].map((_, index) => (
                <span
                  key={index}
                  style={{
                    "--i": index,
                    "--petal-left": `${4 + ((index * 13) % 92)}%`,
                    "--petal-size": `${18 + (index % 5) * 4}px`,
                    "--petal-drift-mid": `${((index % 7) - 3) * 16}px`,
                    "--petal-drift-end": `${((index % 9) - 4) * 24}px`,
                  }}
                >
                  {["✿", "✽", "✼", "❀"][index % 4]}
                </span>
              ))}
            </div>
          ) : null}
          <div className="curtain-title-mark">
            <div className="seal-emblem">DP</div>
            <p className="seal-guest">For {guestName}</p>
          </div>
        </section>
      )}

      <div className="experience-shell">
        <section className="media-showcase media-showcase-hero">
          <div className="section-heading royal-heading">
            <div className="hero-film-actions">
              {isHostPreview && <p className="host-preview-pill">Host Preview Mode</p>}
              {!isHostPreview && (
                <button type="button" className={rsvpButtonClass} onClick={handleRSVPClick}>
                  {rsvpButtonText}
                </button>
              )}
            </div>
          </div>

          <div className="slider-wrapper royal-slider-wrapper">
            {loadingInvite ? (
              <p className="muted-text">Loading invite data...</p>
            ) : inviteError ? (
              <p className="error-text">{inviteError}</p>
            ) : (
              <ExperienceMediaSlider
                mainMedia={mainMedia}
                additionalMedia={mediaList}
                isLoggedIn={isLoggedIn}
                onLoginPrompt={() => setShowLoginModal(true)}
                shouldAutoplayPrimary={isInvitationOpened}
                clientConfig={{
                  ...sliderClientConfig,
                  maxHeight: sliderClientConfig.maxHeight || 760,
                  minHeight: sliderClientConfig.minHeight || 520,
                }}
              />
            )}
          </div>
        </section>

        <section className="ceremony-panel">
          <div className="ceremony-copy">
            <p className="eyebrow-text">Event Details</p>
            <h2>{eventMeta.title}</h2>
            <p className="muted-text">
              Review the invitation, event details, and RSVP options for this celebration.
            </p>
            <p className="muted-text">
              A note from the hosts: we would love for you to join us and share your wishes.
            </p>
          </div>
          <div className="ceremony-divider"></div>
        </section>

        {loadingComments && <p className="muted-text">Loading comments...</p>}
        {commentError && <p className="error-text">{commentError}</p>}

        {isLoggedIn ? (
          <InviteCommentSection
            comments={comments}
            currentUser={currentUser}
            onSubmitComment={handleCommentSubmit}
            onLikeToggle={handleLikeToggle}
            onDeleteComment={handleDeleteComment}
            pendingDeleteIds={pendingDeleteIds}
          />
        ) : (
          <div className="login-cta-card">
            <p>Login to see what other guests are saying and add your own wishes.</p>
            <button type="button" className="primary-btn" onClick={() => setShowLoginModal(true)}>
              Login / Sign up
            </button>
          </div>
        )}

        <footer className="invite-site-footer">
          <p>Create your own video invitation with DesiInvite.</p>
          <div className="invite-site-footer-actions">
            <a href="/">Explore DesiInvite</a>
            <a href="/templates">Browse templates</a>
          </div>
        </footer>
      </div>

      {showLoginModal && (
        <LoginModal onClose={() => setShowLoginModal(false)} onSuccess={handleLoginSuccess} />
      )}

      {showRSVPModal && (
        <div className="experience-modal-overlay" role="dialog" aria-modal="true">
          <div className="experience-modal-body">
            <button
              type="button"
              className="experience-modal-close"
              aria-label="Close RSVP modal"
              onClick={() => setShowRSVPModal(false)}
            >
              ×
            </button>
          <RSVPModal
            guest={guestForRSVP}
            onClose={() => setShowRSVPModal(false)}
            onSuccess={handleRSVPSuccess}
          />
          </div>
        </div>
      )}
    </div>
  );
}
